import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';

export const ONBOARDING_VERSION = '2026-01-06';

export const onboardingSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Gabhan ERP',
    description: "Let's personalize your workspace in 60 seconds.",
    subtitle: 'Smart inventory tracking\nMulti-channel sync\nReal-time analytics',
    cta: 'Continue',
    category: 'setup',
    estimatedSeconds: 10,
  },
  {
    id: 'org',
    title: 'Confirm Organization',
    description: 'Name your default organization and timezone.',
    cta: 'Continue',
    category: 'setup',
    estimatedSeconds: 20,
  },
  {
    id: 'product',
    title: 'Create First Product',
    description: 'Add a SKU with cost and selling price.',
    cta: 'Create product',
    action: { type: 'route', to: '/inventory/products/new' },
    category: 'data',
    estimatedSeconds: 30,
    optional: false,
  },
  {
    id: 'warehouse',
    title: 'Set Up Warehouse',
    description: 'Create your first warehouse location.',
    cta: 'Create warehouse',
    action: { type: 'route', to: '/warehouses/new' },
    category: 'data',
    estimatedSeconds: 20,
    optional: false,
  },
  {
    id: 'done',
    title: "You're ready! 🎉",
    description: 'Next: connect Amazon and create your first PO.',
    cta: 'Finish',
    category: 'complete',
    estimatedSeconds: 5,
  },
];

const STORAGE_KEY = `gabhan:onboarding:${ONBOARDING_VERSION}`;
const ANALYTICS_KEY = `gabhan:onboarding:analytics:${ONBOARDING_VERSION}`;

const defaultState = {
  version: ONBOARDING_VERSION,
  open: true,
  stepIndex: 0,
  completed: false,
  skipped: false,
  updatedAt: null,
  completedSteps: [], // track individual step completions
};

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// ── Analytics tracking ────────────────────────────────────
function _trackOnboardingEvent(event, data = {}) {
  try {
    const analytics = safeParse(localStorage.getItem(ANALYTICS_KEY)) || { events: [] };
    analytics.events.push({ event, ...data, timestamp: Date.now() });
    // Cap at 100 events
    if (analytics.events.length > 100) analytics.events = analytics.events.slice(-100);
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(analytics));
  } catch (_e) { /* safe */ }

  try {
    window.dispatchEvent(new CustomEvent('onboarding-event', { detail: { event, ...data } }));
  } catch (_e) { /* safe */ }
}

/**
 * Get onboarding analytics data for reporting.
 */
export function getOnboardingAnalytics() {
  return safeParse(localStorage.getItem(ANALYTICS_KEY)) || { events: [] };
}

export function useOnboarding({ userId }) {
  const [state, setState] = useState(() => {
    const saved = safeParse(localStorage.getItem(STORAGE_KEY));
    return saved ? { ...defaultState, ...saved } : defaultState;
  });

  const mountedRef = useRef(true);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, updatedAt: Date.now() }));
  }, [state]);

  // Track time spent when the onboarding panel is open
  useEffect(() => {
    if (state.open) {
      startTimeRef.current = Date.now();
    } else if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      _trackOnboardingEvent('panel_closed', { durationMs: elapsed, stepIndex: state.stepIndex });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open]);

  const currentStep = useMemo(
    () => onboardingSteps[state.stepIndex] ?? onboardingSteps[0],
    [state.stepIndex]
  );

  // ── Progress metrics ────────────────────────────────────
  const progress = useMemo(() => {
    const total = onboardingSteps.length;
    const current = state.stepIndex;
    const pct = total > 0 ? Math.round((current / (total - 1)) * 100) : 0;
    const completedIds = state.completedSteps || [];
    const estimatedRemainingSeconds = onboardingSteps
      .slice(current)
      .reduce((sum, s) => sum + (s.estimatedSeconds || 15), 0);

    return {
      current,
      total,
      percentage: Math.min(100, pct),
      completedStepIds: completedIds,
      isComplete: state.completed,
      estimatedRemainingSeconds,
    };
  }, [state.stepIndex, state.completed, state.completedSteps]);

  // ── Core actions ────────────────────────────────────────
  const patch = useCallback(async (partial) => {
    setState((s) => {
      const updated = { ...s, ...partial, updatedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      // Fire-and-forget backend sync
      if (userId) {
        api.post('/onboarding/progress', updated).catch(() => {});
      }

      return updated;
    });
  }, [userId]);

  const open = useCallback(() => {
    _trackOnboardingEvent('opened', { stepIndex: state.stepIndex });
    patch({ open: true });
  }, [patch, state.stepIndex]);

  const close = useCallback(() => {
    _trackOnboardingEvent('closed', { stepIndex: state.stepIndex });
    patch({ open: false });
  }, [patch, state.stepIndex]);

  const skip = useCallback(() => {
    _trackOnboardingEvent('skipped', { stepIndex: state.stepIndex, stepId: currentStep?.id });
    setState((s) => {
      const newState = { ...s, open: false, skipped: true, updatedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      if (userId) {
        api.post('/onboarding/dismiss', {}).catch(() => {});
      }
      return newState;
    });
  }, [userId, state.stepIndex, currentStep]);

  const nextStep = useCallback(() => {
    setState((s) => {
      const nextIndex = Math.min(s.stepIndex + 1, onboardingSteps.length - 1);
      const completed = nextIndex === onboardingSteps.length - 1;
      const completedSteps = [...new Set([...(s.completedSteps || []), onboardingSteps[s.stepIndex]?.id])];
      const updated = {
        ...s,
        stepIndex: nextIndex,
        completed,
        completedSteps,
        open: true,
        updatedAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      _trackOnboardingEvent('step_completed', {
        stepId: onboardingSteps[s.stepIndex]?.id,
        nextStepId: onboardingSteps[nextIndex]?.id,
        stepIndex: nextIndex,
      });

      if (userId) {
        api.post('/onboarding/complete-item', { stepId: onboardingSteps[nextIndex]?.id }).catch(() => {});
      }

      return updated;
    });
  }, [userId]);

  const prevStep = useCallback(() => {
    _trackOnboardingEvent('step_back', { stepIndex: state.stepIndex });
    patch({ stepIndex: Math.max(state.stepIndex - 1, 0) });
  }, [patch, state.stepIndex]);

  const finish = useCallback(() => {
    const elapsed = Date.now() - (startTimeRef.current || Date.now());
    _trackOnboardingEvent('completed', { totalDurationMs: elapsed, stepCount: onboardingSteps.length });

    setState((s) => {
      const completedSteps = [...new Set([...(s.completedSteps || []), ...onboardingSteps.map(st => st.id)])];
      const newState = { ...s, completed: true, open: false, completedSteps, updatedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      if (userId) {
        api.post('/onboarding/complete', {}).catch(() => {});
      }
      return newState;
    });
  }, [userId]);

  const setStep = useCallback(
    (idx) => {
      const clamped = Math.max(0, Math.min(idx, onboardingSteps.length - 1));
      _trackOnboardingEvent('step_jump', { from: state.stepIndex, to: clamped });
      patch({ stepIndex: clamped });
    },
    [patch, state.stepIndex]
  );

  /**
   * Reset onboarding to initial state (useful for testing / re-triggering).
   */
  const reset = useCallback(() => {
    _trackOnboardingEvent('reset');
    const fresh = { ...defaultState, updatedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    setState(fresh);
    if (userId) {
      api.post('/onboarding/reset', {}).catch(() => {});
    }
  }, [userId]);

  /**
   * Check if a specific step has been completed.
   */
  const isStepCompleted = useCallback(
    (stepId) => (state.completedSteps || []).includes(stepId),
    [state.completedSteps]
  );

  /**
   * Can the user move forward? (validation gate)
   */
  const canProceed = useMemo(() => {
    // Last step → only finish is available
    if (state.stepIndex >= onboardingSteps.length - 1) return false;
    return true;
  }, [state.stepIndex]);

  return {
    state,
    steps: onboardingSteps,
    currentStep,
    progress,

    // Navigation
    open,
    close,
    skip,
    next: nextStep,
    prev: prevStep,
    finish,
    setStep,
    reset,

    // Helpers
    isStepCompleted,
    canProceed,
    getAnalytics: getOnboardingAnalytics,
  };
}
